import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { ICellRendererParams } from '@ag-grid-community/core';
import { DesignSystemTextProps, BodyTextSmall, FlexRow, FlexCol } from '@minecraft.atoms';
import { MediaTypeCodeEnum } from '@minecraft.graphql-operations';
import { StyledCenteredCell, StyledCheckbox, StyledLeftCell } from '@minecraft.grid';
import Icon from '@minecraft.icon';
import { useTranslation } from '@blocs.i18n';
import { FileTypeWithMeta } from './types';

const StyledPlayIcon = styled(Icon)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  max-height: 50%;
  height: 20px;
  width: 20px;
  cursor: pointer;
`;

const CommonMediaStyle = `
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const StyledVideoTag = styled.video<{ $isMobile?: boolean }>`
  ${CommonMediaStyle}
  cursor: pointer;
  max-height: ${({ $isMobile }) => ($isMobile ? '150px' : '56px')};
  max-width: ${({ $isMobile }) => ($isMobile ? '150px' : '88px')};
`;

const StyledAudioTag = styled.audio<{ $isMobile?: boolean }>`
  ${CommonMediaStyle}
  cursor: pointer;
  max-height: ${({ $isMobile }) => ($isMobile ? '150px' : 'auto')};
`;

const StyledAudioElWrapper = styled.div`
  position: relative;
  max-height: 56px;
  max-width: 88px;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.designSystem.bgColors.disabled};
`;

const StyledImagePreviewTag = styled.img`
  max-height: 56px;
  max-width: 88px;
  object-fit: contain;
  margin-right: 8px;
  margin-left: 8px;
`;

type ThumbnailCellProps = {
  data: FileTypeWithMeta;
  currentMediaType: MediaTypeCodeEnum;
  isMobile?: boolean;
};

type CheckboxCellProps = Pick<ICellRendererParams, 'node'> & {
  selectedFileIds: string[];
  toggleFileSelection: (fileId: string) => void;
  isMobile?: boolean;
};

type MobileCellProps = ICellRendererParams & {
  data: FileTypeWithMeta;
  selectedFileIds: string[];
  toggleFileSelection: (fileId: string) => void;
  currentMediaType: MediaTypeCodeEnum;
};

type BaseCellProps = {
  data: FileTypeWithMeta;
  isMobile?: boolean;
};

const WithLeftCellWrapper: FC<{ withWrapper?: boolean }> = ({ children, withWrapper }) => {
  return withWrapper ? <StyledLeftCell>{children}</StyledLeftCell> : <>{children}</>;
};

const WithMobileSizingWrapper: FC<{ isMobile?: boolean; color?: DesignSystemTextProps['color'] }> = ({
  children,
  isMobile,
  color,
}) => {
  return isMobile ? <BodyTextSmall color={color}>{children}</BodyTextSmall> : <>{children}</>;
};

export const ThumbnailCell = ({ data, currentMediaType, isMobile }: ThumbnailCellProps) => {
  const [isPlayIconHidden, setIsPlayIconHidden] = useState(false);
  const mediaElRef = React.useRef<HTMLVideoElement>(null);

  const handleVideoPreview = async () => {
    const mediaEl = mediaElRef.current;
    const isCurrentMediaTypeVideo = currentMediaType === MediaTypeCodeEnum.Video;

    if (!mediaEl) return;

    if (mediaEl.paused || mediaEl.ended) {
      await mediaEl.play();
      isCurrentMediaTypeVideo && setIsPlayIconHidden(true);
    } else {
      mediaEl.pause();
      isCurrentMediaTypeVideo && setIsPlayIconHidden(false);
    }
  };

  if (currentMediaType === MediaTypeCodeEnum.Photo) {
    return (
      <StyledLeftCell>
        <StyledImagePreviewTag src={data?.fileThumbnailSrc} alt={data?.fileName} />
      </StyledLeftCell>
    );
  }

  if (currentMediaType === MediaTypeCodeEnum.Audio) {
    return (
      <StyledLeftCell className="cn_relative cn_w-full">
        <StyledAudioElWrapper>
          <StyledAudioTag ref={mediaElRef} src={data?.fileThumbnailSrc} controls={false}>
            <track kind="captions" />
          </StyledAudioTag>
          <StyledPlayIcon name="audio" onClick={handleVideoPreview} />
        </StyledAudioElWrapper>
      </StyledLeftCell>
    );
  }

  if (currentMediaType === MediaTypeCodeEnum.Document) {
    return (
      <StyledLeftCell className="cn_relative cn_w-full">
        <StyledAudioElWrapper>
          <StyledPlayIcon name="document" />
        </StyledAudioElWrapper>
      </StyledLeftCell>
    );
  }

  if (currentMediaType === MediaTypeCodeEnum.Video) {
    return (
      <StyledLeftCell className="cn_relative">
        <StyledVideoTag
          src={data?.videoFileSrc}
          poster={data?.fileThumbnailSrc}
          controls={false}
          ref={mediaElRef}
          onClick={handleVideoPreview}
          $isMobile={isMobile}
        >
          <track kind="captions" />
        </StyledVideoTag>
        {!isPlayIconHidden && <StyledPlayIcon name="play-circle-filled" onClick={handleVideoPreview} />}
      </StyledLeftCell>
    );
  }

  return null;
};

export const CheckboxCell: FC<CheckboxCellProps> = ({ isMobile, selectedFileIds, toggleFileSelection, node }) => {
  const { t } = useTranslation();
  const isSelected = selectedFileIds.includes(node?.id || '');
  const toggleCheckbox = useCallback(() => {
    toggleFileSelection(node?.id);
  }, [node?.id, toggleFileSelection]);

  const label = isSelected ? t('common:form.label.deselect') : t('common:form.label.select');

  if (isMobile) {
    return (
      <StyledLeftCell>
        <StyledCheckbox aria-label={label} isChecked={isSelected} disabled={false} onChange={toggleCheckbox} />
      </StyledLeftCell>
    );
  }

  return (
    <StyledCenteredCell className="cn_atom_grow-0">
      <StyledCheckbox aria-label={label} isChecked={isSelected} disabled={false} onChange={toggleCheckbox} />
    </StyledCenteredCell>
  );
};

export const NameCell = ({ data, isMobile }: BaseCellProps) => {
  const { t } = useTranslation();

  return (
    <WithLeftCellWrapper withWrapper={!isMobile}>
      <FlexCol gap="2">
        <FlexRow alignItems="center">
          <WithMobileSizingWrapper isMobile={isMobile}>{data.fileName}</WithMobileSizingWrapper>
        </FlexRow>
        {data?.uploadError && (
          <FlexRow gap="1" alignItems="center" textColor="statusRejected">
            <Icon height={1.0} themeColor="danger" name="alert" />
            <WithMobileSizingWrapper color="statusRejected" isMobile={isMobile}>
              {data.uploadError}
            </WithMobileSizingWrapper>
          </FlexRow>
        )}
        {data?.uploadSuccess === true && (
          <FlexRow gap="1" alignItems="center" textColor="statusActive">
            <Icon height={1.0} themeColor="success" name="check-circle" />
            <WithMobileSizingWrapper color="statusActive" isMobile={isMobile}>
              {t('common:toast.mediaUploadedSuccess')}
            </WithMobileSizingWrapper>
          </FlexRow>
        )}
      </FlexCol>
    </WithLeftCellWrapper>
  );
};

export const FileTypeCell = ({ data, isMobile }: BaseCellProps) => {
  return (
    <WithLeftCellWrapper withWrapper={!isMobile}>
      <WithMobileSizingWrapper isMobile={isMobile}>{data.fileType}</WithMobileSizingWrapper>
    </WithLeftCellWrapper>
  );
};

export const MobileCell = ({ data, node, selectedFileIds, toggleFileSelection, currentMediaType }: MobileCellProps) => {
  const { t } = useTranslation();

  return (
    <FlexCol className="cn_w-full cn_atom_py-2 cn_atom_px-4 cn_mb-4" borderPlacement="all" borderRadius="sm">
      <FlexRow className="cn_w-full cn_atom_pb-2" gap="4" borderPlacement="bottom" wrap="nowrap">
        <CheckboxCell
          isMobile
          selectedFileIds={selectedFileIds}
          toggleFileSelection={toggleFileSelection}
          node={node}
        />
        <ThumbnailCell isMobile data={data} currentMediaType={currentMediaType} />
      </FlexRow>
      <FlexCol className="cn_w-full cn_atom_py-2" gap="2" borderPlacement="bottom">
        <BodyTextSmall fontWeight="semibold">{t('common:form.label.name')}</BodyTextSmall>
        <NameCell isMobile data={data} />
      </FlexCol>
      <FlexCol className="cn_w-full cn_atom_py-2" gap="2" borderPlacement="bottom">
        <BodyTextSmall fontWeight="semibold">{t('common:agGrid.format')}</BodyTextSmall>
        <FileTypeCell isMobile data={data} />
      </FlexCol>
    </FlexCol>
  );
};
